import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Quote, Star } from 'lucide-react';

// Idle time before auto-scroll resumes after user interaction.
const RESUME_AFTER_MS = 2500;
// Pixels per animation frame (~30 px/sec at 60 fps).
const AUTO_STEP_PX = 0.5;

// 5 cyan stars with a white glow so it reads as premium/on-brand.
function StarRow({ rating = 5 }) {
  return (
    <div className="flex items-center gap-0.5 mb-2" aria-label={`${rating} out of 5 stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={`w-3.5 h-3.5 ${
            i < rating
              ? 'fill-cyan-300 text-cyan-300 [filter:drop-shadow(0_0_3px_rgba(255,255,255,0.7))]'
              : 'text-white/20'
          }`}
        />
      ))}
    </div>
  );
}

// The strip renders each review twice back-to-back so the auto-scroll can
// wrap seamlessly without visible jumps.
function Strip({ reviews, ariaHidden = false }) {
  return (
    <div
      className="flex items-stretch gap-6 sm:gap-8 shrink-0 pr-6 sm:pr-8"
      aria-hidden={ariaHidden || undefined}
    >
      {reviews.map((r, i) => (
        <figure
          key={`${r.name}-${i}`}
          className="shrink-0 w-[300px] sm:w-[360px] bg-white/[0.03] border border-white/10 rounded-lg px-5 py-4 flex flex-col transition-all duration-300 hover:border-cyan-400/30 hover:-translate-y-0.5"
        >
          <StarRow rating={r.rating ?? 5} />
          <Quote className="w-4 h-4 text-cyan-400/70 shrink-0 mb-2" />
          <blockquote className="text-white/85 text-[13px] leading-relaxed flex-1">
            {r.quote}
          </blockquote>
          <figcaption className="mt-3 pt-3 border-t border-white/5">
            <div className="font-tech text-cyan-300 text-[11px] uppercase tracking-[0.16em]">
              {r.name}
            </div>
            <div className="text-white/55 text-[11px] mt-0.5">{r.attribution}</div>
            {r.model && (
              <div className="text-cyan-400/70 text-[10px] mt-1 italic">
                {r.model}
              </div>
            )}
          </figcaption>
        </figure>
      ))}
    </div>
  );
}

/**
 * ReviewsMarquee — scrolling bar of customer reviews.
 *
 * Scroll behaviour:
 *   - Auto-scrolls slowly in the given direction (rAF-driven scrollLeft).
 *   - User can pan/drag/swipe horizontally at any time to jump back or
 *     forward. Auto-scroll pauses on interaction and resumes after 2.5s
 *     of idle.
 *   - Content is duplicated so scrollLeft can wrap seamlessly at either end.
 */
export default function ReviewsMarquee({
  title,
  reviews,
  direction = 'ltr',
  testId = 'reviews-marquee',
}) {
  const trackRef = useRef(null);
  const [paused, setPaused] = useState(false);
  const idleTimerRef = useRef(null);

  // Restart the idle timer whenever the user interacts.
  const bumpIdleTimer = useCallback(() => {
    setPaused(true);
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => setPaused(false), RESUME_AFTER_MS);
  }, []);

  // Wrap scrollLeft so auto-scroll loops seamlessly across the duplicated strip.
  const wrapScroll = useCallback((el) => {
    const half = el.scrollWidth / 2;
    if (el.scrollLeft >= half) el.scrollLeft -= half;
    else if (el.scrollLeft < 0) el.scrollLeft += half;
  }, []);

  // Seed initial scrollLeft in the middle so the user can scroll both directions.
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    // Wait a tick for children to lay out so scrollWidth is meaningful.
    const t = setTimeout(() => {
      if (el.scrollWidth > 0) el.scrollLeft = el.scrollWidth / 4;
    }, 50);
    return () => clearTimeout(t);
  }, [reviews]);

  // Auto-scroll ticker.
  useEffect(() => {
    if (paused) return;
    let raf;
    const step = () => {
      const el = trackRef.current;
      if (el) {
        el.scrollLeft += direction === 'ltr' ? AUTO_STEP_PX : -AUTO_STEP_PX;
        wrapScroll(el);
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [paused, direction, wrapScroll]);

  // Keep scrollLeft wrapped even during user drags (fires on native scroll).
  const onScroll = useCallback(() => {
    if (!trackRef.current) return;
    wrapScroll(trackRef.current);
  }, [wrapScroll]);

  return (
    <section
      aria-label={title}
      data-testid={testId}
      className="relative bg-black border-y border-white/5 overflow-hidden py-10 sm:py-12"
    >
      {/* Centered title */}
      <div className="text-center px-5 mb-6 sm:mb-7">
        <span className="font-tech text-cyan-300 text-[10px] sm:text-[11px] uppercase tracking-[0.28em]">
          {title}
        </span>
      </div>

      {/* Full-bleed scroll container — soft 10% edge fade + native pan-x.
          Auto-scroll pauses ONLY when the user actively interacts (touch,
          pointer-down, or wheel scroll). Simply hovering the marquee no
          longer stops the drift — reading a passing card doesn't cost you
          the ambient motion. */}
      <div
        ref={trackRef}
        onScroll={onScroll}
        onPointerDown={bumpIdleTimer}
        onTouchStart={bumpIdleTimer}
        onWheel={bumpIdleTimer}
        className="relative w-full overflow-x-auto overflow-y-hidden cursor-grab active:cursor-grabbing select-none touch-pan-x [&::-webkit-scrollbar]:hidden [scrollbar-width:none]"
        style={{
          maskImage:
            'linear-gradient(90deg, transparent 0%, #000 10%, #000 90%, transparent 100%)',
          WebkitMaskImage:
            'linear-gradient(90deg, transparent 0%, #000 10%, #000 90%, transparent 100%)',
        }}
      >
        <div className="flex">
          <Strip reviews={reviews} />
          <Strip reviews={reviews} ariaHidden />
        </div>
      </div>
    </section>
  );
}
